import type {
  DraftPreviewRequest,
  TemplateDetail,
  TemplateDraftUpdate,
  TemplateVersion,
} from "../../lib/queries/templates";
import {
  parseBlocksJson,
  parseThemeJson,
  serializeBlocksJson,
  serializeThemeJson,
  type EditorDoc,
  type ThemeState,
} from "./blocksModel";

/**
 * The whole editable state of the template editor, held in React and
 * serialized back to the wire shape on save / preview. Initialized from the
 * draft version when one exists, else from the current published version.
 */
export interface EditorState {
  subject: string;
  preheader: string;
  /** kind=code */
  htmlBody: string;
  textBody: string;
  /** kind=blocks */
  doc: EditorDoc;
  theme: ThemeState;
  /** Raw JSON text of the sample data (edited as text, validated on use). */
  sampleData: string;
}

function prettyJson(raw: string): string {
  try {
    return JSON.stringify(JSON.parse(raw), null, 2);
  } catch {
    return raw;
  }
}

export function initEditorState(detail: TemplateDetail): EditorState {
  const src: TemplateVersion | undefined = detail.draft ?? detail.current;
  return {
    subject: src?.subject_tmpl ?? "",
    preheader: src?.preheader_tmpl ?? "",
    htmlBody: src?.html_body ?? "",
    textBody: src?.text_body ?? "",
    doc: parseBlocksJson(src?.blocks_json),
    theme: parseThemeJson(src?.theme_json),
    sampleData: prettyJson(detail.sample_data_json || "{}"),
  };
}

/**
 * Canonical snapshot used for dirty tracking — compares what would actually
 * be saved, so reordering back to the original or retyping the same value
 * clears the unsaved flag.
 */
export function snapshot(state: EditorState, kind: TemplateDetail["kind"]): string {
  return JSON.stringify(buildDraftUpdate(state, kind));
}

export function buildDraftUpdate(
  state: EditorState,
  kind: TemplateDetail["kind"],
): TemplateDraftUpdate {
  const base: TemplateDraftUpdate = {
    subject_tmpl: state.subject,
    preheader_tmpl: state.preheader,
    sample_data_json: state.sampleData,
  };
  if (kind === "blocks") {
    base.blocks_json = serializeBlocksJson(state.doc);
    base.theme_json = serializeThemeJson(state.theme);
  } else {
    base.html_body = state.htmlBody;
    base.text_body = state.textBody;
  }
  return base;
}

/** Parses the sample-data JSON text; `error` is set when it is invalid. */
export function parseSampleData(raw: string): {
  data: unknown;
  error: string | null;
} {
  const trimmed = raw.trim();
  if (trimmed === "") return { data: {}, error: null };
  try {
    return { data: JSON.parse(trimmed), error: null };
  } catch {
    return { data: undefined, error: "Invalid JSON." };
  }
}

export function buildPreviewRequest(
  state: EditorState,
  kind: TemplateDetail["kind"],
  channel: NonNullable<DraftPreviewRequest["channel"]>,
  data: unknown,
  marketing: boolean,
): DraftPreviewRequest {
  // An SMS preview renders the text body alone and answers with its segment
  // count; a subject, preheader and HTML have nowhere to go.
  if (channel === "sms") {
    return { kind, channel, text_body: state.textBody, data };
  }
  const req: DraftPreviewRequest = {
    kind,
    channel,
    subject_tmpl: state.subject,
    preheader_tmpl: state.preheader,
    data,
    marketing,
  };
  if (kind === "blocks") {
    req.blocks_json = serializeBlocksJson(state.doc);
    req.theme_json = serializeThemeJson(state.theme);
  } else {
    req.html_body = state.htmlBody;
    req.text_body = state.textBody;
  }
  return req;
}
